<template>
    <div aria-live="polite">
        <nav v-if="showFamilyRail" class="mb-6 flex flex-wrap gap-2" aria-label="Типи публікацій" data-hashtag-family-rail>
            <button
                v-for="option in visibleFamilyOptions"
                :key="option.value"
                type="button"
                :class="[
                    'rounded-full border px-4 py-2 text-sm font-semibold transition-colors',
                    activeFilter === option.value
                        ? 'border-foxy-action bg-foxy-action text-white'
                        : 'border-foxy-border bg-foxy-surface text-foxy-text hover:border-foxy-action/60',
                ]"
                :aria-pressed="activeFilter === option.value ? 'true' : 'false'"
                :data-hashtag-family="option.value"
                @click="changeFilter(option.value)"
            >
                {{ option.label }} · {{ option.count }}
            </button>
        </nav>

        <div v-if="loading && items.length === 0" class="space-y-4" data-hashtag-state="loading" role="status">
            <div v-for="n in 3" :key="n" class="foxy-card h-40 animate-pulse bg-foxy-elevated"></div>
        </div>

        <section v-else-if="error && items.length === 0" class="foxy-panel p-8 text-center" data-hashtag-state="error" role="alert">
            <h2 class="text-xl font-bold text-foxy-text">Не вдалося завантажити публікації</h2>
            <p class="mt-2 text-foxy-text-secondary">
                {{ invalidCursor ? 'Посилання продовження застаріло або пошкоджене.' : 'Спробуйте ще раз.' }}
            </p>
            <button type="button" class="foxy-btn-secondary mt-5 px-5 py-2.5" @click="invalidCursor ? restart() : retry()">
                {{ invalidCursor ? 'Почати спочатку' : 'Спробувати ще раз' }}
            </button>
        </section>

        <section v-else-if="items.length === 0" class="foxy-panel p-10 text-center" data-hashtag-state="empty">
            <i class="fas fa-hashtag text-4xl text-foxy-action" aria-hidden="true"></i>
            <p class="mt-4 text-foxy-text-secondary">Поки немає доступних публікацій із цим хештегом.</p>
        </section>

        <template v-else>
            <transition-group name="hashtag-card" tag="div" class="space-y-4">
                <div v-for="item in items" :key="item.key">
                    <feed-card :item="item" />
                    <nav
                        v-if="Array.isArray(item.hashtags) && item.hashtags.length"
                        class="mt-2 flex flex-wrap gap-2 px-2"
                        aria-label="Хештеги публікації"
                        data-card-hashtags
                    >
                        <a
                            v-for="hashtag in item.hashtags"
                            :key="hashtag.slug"
                            :href="hashtag.url"
                            class="rounded-full bg-foxy-elevated px-3 py-1 text-sm font-semibold text-foxy-action hover:bg-foxy-action/10"
                            data-card-hashtag
                        >
                            #{{ hashtag.name }}
                        </a>
                    </nav>
                </div>
            </transition-group>

            <section v-if="error" class="foxy-panel mt-6 p-6 text-center" role="alert" data-hashtag-state="continuation-error">
                <p class="font-semibold text-foxy-text">Не вдалося завантажити наступні публікації.</p>
                <button type="button" class="foxy-btn-secondary mt-4 px-5 py-2.5" @click="retry">Спробувати ще раз</button>
            </section>

            <div v-else-if="hasMore" class="mt-6 text-center" data-hashtag-state="more">
                <button type="button" class="foxy-btn-secondary px-6 py-3" :disabled="loading" @click="loadMore">
                    <i v-if="loading" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    {{ loading ? 'Завантажуємо…' : 'Завантажити ще' }}
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import FeedCard from './FeedCard.vue';

export default {
    name: 'HashtagContributionList',
    components: { FeedCard },
    props: {
        initialPage: { type: Object, required: true },
        initialFilter: { type: String, default: 'all' },
        endpoint: { type: String, required: true },
    },
    data() {
        return {
            items: Array.isArray(this.initialPage.items) ? [...this.initialPage.items] : [],
            counts: { total: 0, review: 0, discussion: 0, collection: 0, ...(this.initialPage.counts || {}) },
            nextCursor: this.initialPage.next_cursor || null,
            hasMore: Boolean(this.initialPage.has_more),
            activeFilter: this.initialPage.canonical_filter || this.initialFilter,
            loading: false,
            error: null,
            invalidCursor: false,
            lastFailedCursor: null,
            generation: 0,
        };
    },
    computed: {
        familyOptions() {
            return [
                { value: 'all', label: 'Усі', count: Number(this.counts.total || 0) },
                { value: 'review', label: 'Рецензії та відгуки', count: Number(this.counts.review || 0) },
                { value: 'discussion', label: 'Дискусії', count: Number(this.counts.discussion || 0) },
                { value: 'collection', label: 'Добірки', count: Number(this.counts.collection || 0) },
            ];
        },
        visibleFamilyOptions() {
            return this.familyOptions.filter((option) => option.value === 'all' || option.count > 0);
        },
        visibleFamilyCount() {
            return this.familyOptions.filter((option) => option.value !== 'all' && option.count > 0).length;
        },
        showFamilyRail() {
            return this.visibleFamilyCount >= 2;
        },
    },
    mounted() {
        this.canonicalizeUrl();
    },
    methods: {
        async changeFilter(filter) {
            if (filter === this.activeFilter || this.loading) return false;

            this.activeFilter = filter;
            this.items = [];
            this.nextCursor = null;
            this.hasMore = true;
            this.error = null;
            this.invalidCursor = false;
            this.canonicalizeUrl();

            return this.requestPage(null, true, ++this.generation);
        },
        loadMore() {
            if (!this.hasMore || !this.nextCursor || this.loading) return false;
            return this.requestPage(this.nextCursor, false, this.generation);
        },
        retry() {
            return this.requestPage(this.lastFailedCursor, this.items.length === 0, this.generation);
        },
        restart() {
            this.items = [];
            this.nextCursor = null;
            this.hasMore = true;
            this.error = null;
            this.invalidCursor = false;
            return this.requestPage(null, true, ++this.generation);
        },
        async requestPage(cursor, replace, generation) {
            if (this.loading) return false;

            this.loading = true;
            this.error = null;
            this.invalidCursor = false;
            this.lastFailedCursor = cursor;
            const requestedFilter = this.activeFilter;
            const params = { family: requestedFilter };
            if (cursor) params.cursor = cursor;

            try {
                const response = await axios.get(this.endpoint, {
                    params,
                    headers: { Accept: 'application/json' },
                });
                if (generation !== this.generation || requestedFilter !== this.activeFilter) return false;

                const page = response.data;
                if (page.canonical_filter !== requestedFilter || !Array.isArray(page.items)) {
                    throw new Error('Unexpected Hashtag discovery response');
                }

                const existing = replace ? [] : this.items;
                const seen = new Set(existing.map((item) => item.key));
                const incoming = page.items.filter((item) => item && typeof item.key === 'string' && !seen.has(item.key) && seen.add(item.key));
                this.items = [...existing, ...incoming];
                this.counts = { ...this.counts, ...(page.counts || {}) };
                this.nextCursor = page.next_cursor || null;
                this.hasMore = Boolean(page.has_more);

                return true;
            } catch (error) {
                if (generation !== this.generation || requestedFilter !== this.activeFilter) return false;
                this.error = error;
                this.invalidCursor = this.isInvalidCursor(error);
                return false;
            } finally {
                if (generation === this.generation) this.loading = false;
            }
        },
        isInvalidCursor(error) {
            if (!error || !error.response || error.response.status !== 422) return false;
            const messages = error.response.data && error.response.data.errors && error.response.data.errors.cursor;

            return Array.isArray(messages) && messages.some((message) => String(message).includes('invalid_hashtag_cursor'));
        },
        canonicalizeUrl() {
            const url = new URL(window.location.href);
            ['cursor', 'page', 'per_page', 'limit', 'sort'].forEach((key) => url.searchParams.delete(key));
            if (this.activeFilter === 'all') url.searchParams.delete('family');
            else url.searchParams.set('family', this.activeFilter);
            window.history.replaceState(window.history.state || {}, '', url.toString());
        },
    },
};
</script>

<style scoped>
.hashtag-card-enter-active,
.hashtag-card-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.hashtag-card-enter,
.hashtag-card-leave-to {
    opacity: 0;
    transform: translateY(0.5rem);
}
</style>
